import { For, Show, createSignal } from "solid-js";
import { CloseIcon } from "./icons.tsx";
import styles from "./Tags.module.scss";

export type TagListProps = {
  tags: readonly string[];
  /** Names the list for assistive technology. */
  label: string;
  /** Shown instead of nothing when the list is empty; leave out to show nothing. */
  emptyLabel?: string;
};

/** A product's tags as a row of small chips: hosting region, licence, and the like. */
export const TagList = (props: TagListProps) => (
  <Show
    when={props.tags.length > 0}
    fallback={
      <Show when={props.emptyLabel}>
        {(label) => <span class={styles.empty}>{label()}</span>}
      </Show>
    }
  >
    <ul class={styles.list} aria-label={props.label}>
      <For each={props.tags}>{(tag) => <li class={styles.tag}>{tag}</li>}</For>
    </ul>
  </Show>
);

export type TagEditorProps = {
  label: string;
  hint?: string;
  tags: readonly string[];
  onChange: (tags: string[]) => void;
  /** Names the add action and the remove action for assistive technology. */
  addLabel: string;
  removeLabel: (tag: string) => string;
  placeholder?: string;
};

/**
 * Cleans a typed tag: trims it, and drops it when it is empty or already
 * present, comparing without case so "OSS" and "oss" are one tag.
 */
export const addTag = (tags: readonly string[], typed: string): string[] => {
  const tag = typed.trim();
  if (tag === "") return [...tags];
  if (tags.some((known) => known.localeCompare(tag, undefined, { sensitivity: "accent" }) === 0)) {
    return [...tags];
  }
  return [...tags, tag];
};

/**
 * The chips with a remove button each, and one field to type the next tag
 * into. Enter or a comma adds it; Backspace on an empty field removes the last.
 */
export const TagEditor = (props: TagEditorProps) => {
  const [typed, setTyped] = createSignal("");

  const commit = () => {
    const next = addTag(props.tags, typed());
    setTyped("");
    if (next.length !== props.tags.length) props.onChange(next);
  };

  const onKey = (event: KeyboardEvent) => {
    if (event.key === "Enter" || event.key === ",") {
      event.preventDefault();
      commit();
      return;
    }
    if (event.key === "Backspace" && typed() === "" && props.tags.length > 0) {
      props.onChange(props.tags.slice(0, -1));
    }
  };

  return (
    <div class={styles.editor}>
      <span class={styles.label}>{props.label}</span>
      <div class={styles.field}>
        <For each={props.tags}>
          {(tag) => (
            <span class={styles.tag}>
              {tag}
              <button
                class={styles.remove}
                type="button"
                aria-label={props.removeLabel(tag)}
                title={props.removeLabel(tag)}
                onClick={() => props.onChange(props.tags.filter((known) => known !== tag))}
              >
                <CloseIcon />
              </button>
            </span>
          )}
        </For>
        <input
          class={styles.input}
          type="text"
          aria-label={props.label}
          placeholder={props.placeholder}
          value={typed()}
          onInput={(event) => setTyped(event.currentTarget.value)}
          onKeyDown={onKey}
          onBlur={commit}
        />
        <button class={styles.add} type="button" onClick={commit} disabled={typed().trim() === ""}>
          {props.addLabel}
        </button>
      </div>
      <Show when={props.hint}>{(hint) => <span class={styles.hint}>{hint()}</span>}</Show>
    </div>
  );
};
